import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const CalendarIntegration = ({ eventData, onCalendarSync }) => {
  const [connectedCalendars, setConnectedCalendars] = useState([
    { id: 'google', name: 'Google Calendar', connected: true, email: 'user@gmail.com' },
    { id: 'apple', name: 'Apple Calendar', connected: false, email: null }
  ]);
  const [syncSettings, setSyncSettings] = useState({
    createEvent: true,
    sendInvitations: true,
    setReminders: true,
    shareLocation: true
  });

  // Mock calendar conflicts
  const mockConflicts = [
    {
      id: 1,
      title: "Team Meeting",
      start: "2024-10-26T14:00:00",
      end: "2024-10-26T15:30:00",
      calendar: "Work Calendar"
    },
    {
      id: 2,
      title: "Doctor Appointment",
      start: "2024-10-26T16:00:00",
      end: "2024-10-26T17:00:00",
      calendar: "Personal Calendar"
    }
  ];

  const handleCalendarConnect = (calendarId) => {
    setConnectedCalendars(prev => 
      prev?.map(cal => 
        cal?.id === calendarId 
          ? { ...cal, connected: !cal?.connected, email: cal?.connected ? null : `user@${calendarId}.com` }
          : cal
      )
    );
  };

  const handleSyncSettingChange = (setting, value) => {
    setSyncSettings(prev => ({ ...prev, [setting]: value }));
  };

  const handleSyncToCalendar = () => {
    if (onCalendarSync) {
      onCalendarSync(syncSettings);
    }
  };

  const formatEventDateTime = () => {
    if (!eventData?.date || !eventData?.time) return 'Date and time not set';
    
    const eventDate = new Date(`${eventData.date}T${eventData.time}`);
    return eventDate?.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const hasConflicts = mockConflicts?.length > 0;

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Calendar Integration</h3>
      <div className="space-y-6">
        {/* Event Preview */}
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium text-foreground mb-2">Event Preview</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={14} className="text-muted-foreground" />
              <span className="text-foreground">{formatEventDateTime()}</span>
            </div>
            {eventData?.location && (
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={14} className="text-muted-foreground" />
                <span className="text-foreground">{eventData?.location}</span>
              </div>
            )}
            {eventData?.title && (
              <div className="flex items-center space-x-2">
                <Icon name="Type" size={14} className="text-muted-foreground" />
                <span className="text-foreground">{eventData?.title}</span>
              </div>
            )}
          </div>
        </div>

        {/* Connected Calendars */}
        <div>
          <h4 className="font-medium text-foreground mb-3">Connected Calendars</h4>
          <div className="space-y-3">
            {connectedCalendars?.map(calendar => (
              <div key={calendar?.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${calendar?.connected ? 'bg-success' : 'bg-muted-foreground'}`} />
                  <div>
                    <p className="font-medium text-foreground">{calendar?.name}</p>
                    {calendar?.email && (
                      <p className="text-sm text-muted-foreground">{calendar?.email}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant={calendar?.connected ? "outline" : "default"}
                  size="sm"
                  onClick={() => handleCalendarConnect(calendar?.id)}
                >
                  {calendar?.connected ? 'Disconnect' : 'Connect'}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Sync Settings */}
        <div>
          <h4 className="font-medium text-foreground mb-3">Sync Settings</h4>
          <div className="space-y-3">
            <Checkbox
              label="Create calendar event"
              description="Add this dinner to your calendar"
              checked={syncSettings?.createEvent}
              onChange={(e) => handleSyncSettingChange('createEvent', e?.target?.checked)}
            />
            
            <Checkbox
              label="Send invitations to guests"
              description="Automatically invite selected guests via calendar"
              checked={syncSettings?.sendInvitations}
              onChange={(e) => handleSyncSettingChange('sendInvitations', e?.target?.checked)}
            />
            
            <Checkbox
              label="Set automatic reminders"
              description="Add reminders 1 week, 2 days, and day-of"
              checked={syncSettings?.setReminders}
              onChange={(e) => handleSyncSettingChange('setReminders', e?.target?.checked)}
            />
            
            <Checkbox
              label="Share location details"
              description="Include location information in calendar event"
              checked={syncSettings?.shareLocation}
              onChange={(e) => handleSyncSettingChange('shareLocation', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Calendar Conflicts */}
        {hasConflicts && (
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <h4 className="font-medium text-foreground">Schedule Conflicts</h4>
            </div>
            <div className="space-y-2">
              {mockConflicts?.map(conflict => (
                <div key={conflict?.id} className="p-3 bg-warning/5 border border-warning/20 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-warning-foreground">{conflict?.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(conflict.start)?.toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })} - {new Date(conflict.end)?.toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">{conflict?.calendar}</p>
                    </div>
                    <Icon name="Clock" size={16} className="text-warning" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sync Action */}
        <div className="border-t border-border pt-4">
          <Button
            variant="default"
            onClick={handleSyncToCalendar}
            disabled={!connectedCalendars?.some(cal => cal?.connected) || !eventData?.date || !eventData?.time}
            className="w-full"
          >
            <Icon name="Calendar" size={16} className="mr-2" />
            Sync to Calendar
          </Button>
          
          {!connectedCalendars?.some(cal => cal?.connected) && (
            <p className="text-sm text-muted-foreground mt-2 text-center">
              Connect a calendar to sync your event
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarIntegration;